import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PressFrance } from "@/components/PressFrance";
import { Crematoriums } from "@/components/Crematoriums";
import { Funerals } from "@/components/Funerals";
import { InviteCountdown } from "@/components/InviteCountdown";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Award, Plus, Copy, Link2, Trash2, RefreshCw, Loader2, Clock,
  Users, MousePointerClick, Flame, MessageSquareText, Pencil,
  Mail, Send, Eye, CalendarClock, ShieldCheck, ChevronDown, ChevronRight, CheckCircle2,
} from "lucide-react";

const Section = ({ icon: Icon, title, desc, children, accent = "text-sky-300" }) => (
  <div className="rounded-3xl border border-white/10 bg-card/40 p-5 sm:p-6">
    <div className="mb-1 flex items-center gap-2">
      <Icon size={17} className={accent} />
      <h3 className="text-base font-semibold text-foreground">{title}</h3>
    </div>
    {desc && <p className="mb-4 text-sm text-muted-foreground">{desc}</p>}
    {children}
  </div>
);

const fmt = (d) => { try { return new Date(d).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" }); } catch { return d; } };

export function AmbassadorPanel() {
  const origin = typeof window !== "undefined" ? window.location.origin : "https://bougieducoeur.com";
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [label, setLabel] = useState("");
  const [validHours, setValidHours] = useState(48);
  const [freeCandles, setFreeCandles] = useState(4);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/admin/ambassadors"); setLinks(r.data); }
    catch { setLinks([]); } finally { setLoading(false); }
  };
  useEffect(() => { load(); loadBatches(); }, []);

  const linkFor = (code) => `${origin}/?amb=${code}`;

  const copy = async (val, label = "Copié") => {
    try {
      if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(val); toast.success(label); return; }
      throw new Error("no-clipboard");
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = val; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.focus(); ta.select();
        document.execCommand("copy"); document.body.removeChild(ta);
        toast.success(label);
      } catch { toast.error("Copie impossible"); }
    }
  };

  const PRESETS = [
    { label: "24 h", h: 24 },
    { label: "48 h", h: 48 },
    { label: "72 h", h: 72 },
    { label: "7 jours", h: 168 },
  ];

  const humanDuration = (h) => {
    h = Number(h) || 48;
    if (h % 24 === 0) { const d = h / 24; return d === 1 ? "24 heures" : `${d} jours`; }
    return `${h} heures`;
  };

  const messageFor = (code, fc, hours) => (
`Bonjour,

À l'occasion du prochain lancement de BougieDuCœur, le premier mémorial numérique permettant d'honorer la mémoire de ceux qui nous sont chers, notre Direction a le plaisir de vous réserver une invitation privilégiée et personnelle afin de rejoindre le cercle très limité des Ambassadeurs de notre marque.

Pour vous remercier, nous vous offrons ${fc} bougies mémorielles pour l'éternité, que vous pourrez allumer, quand vous le souhaitez, en mémoire de vos proches depuis votre compte Ambassadeur.

Pour en bénéficier, créez gratuitement votre compte sous ${humanDuration(hours)} :
${linkFor(code)}

Vos ${fc} bougies resteront actives pour l'éternité, sans abonnement.

Toute l'équipe de BougieDuCœur reste à votre écoute et vous souhaite la bienvenue dans cette belle aventure de mémoire et de transmission.

Avec toute notre gratitude,
La Direction
BougieDuCœur
admin@bougieducoeur.com`
  );

  const createLink = async () => {
    if (!label.trim()) { toast.error("Indiquez un nom pour le lien (ex. Famille, Amis, Presse)"); return; }
    setCreating(true);
    try {
      await api.post("/admin/ambassadors", { label: label.trim(), valid_hours: Number(validHours) || 48, free_candles: Number(freeCandles) || 4 });
      toast.success("Lien ambassadeur généré");
      setLabel("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setCreating(false); }
  };
  const renameLink = async (l) => {
    const name = window.prompt("Nom du lien :", l.label || "");
    if (name === null) return;
    if (!name.trim()) { toast.error("Le nom ne peut pas être vide"); return; }
    try { await api.patch(`/admin/ambassadors/${l.id}`, { label: name.trim() }); toast.success("Nom modifié"); load(); }
    catch { toast.error("Erreur"); }
  };
  const renew = async (id) => {
    try { await api.patch(`/admin/ambassadors/${id}`, { renew: true }); toast.success("Validité relancée (48h)"); load(); }
    catch { toast.error("Erreur"); }
  };
  const toggleActive = async (l) => {
    try { await api.patch(`/admin/ambassadors/${l.id}`, { active: !l.active }); load(); }
    catch { toast.error("Erreur"); }
  };
  const remove = async (id) => {
    try { await api.delete(`/admin/ambassadors/${id}`); setLinks((x) => x.filter((l) => l.id !== id)); toast.success("Lien supprimé"); }
    catch { toast.error("Suppression impossible"); }
  };

  // ---------------- Invitations par e-mail ----------------
  const [invEmails, setInvEmails] = useState("");
  const [invLabel, setInvLabel] = useState("");
  const [invFC, setInvFC] = useState(4);
  const [invVH, setInvVH] = useState(48);
  const [invSubject, setInvSubject] = useState("");
  const [consent, setConsent] = useState(false);
  const [scheduleOn, setScheduleOn] = useState(false);
  const [scheduleAt, setScheduleAt] = useState("");
  const [sending, setSending] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [batches, setBatches] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [view, setView] = useState("links");

  const countEmails = () => {
    const parts = (invEmails || "").split(/[\s,;]+/).map((s) => s.trim().toLowerCase()).filter(Boolean);
    const re = /^[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/;
    return new Set(parts.filter((p) => re.test(p))).size;
  };

  const loadBatches = async () => {
    try { const r = await api.get("/admin/ambassadors/invites"); setBatches(r.data || []); }
    catch { setBatches([]); }
  };

  const doPreview = async () => {
    setPreviewing(true);
    try {
      const r = await api.post("/admin/ambassadors/invite/preview", { emails: invEmails, free_candles: Number(invFC) || 4, valid_hours: Number(invVH) || 48 });
      setPreview(r.data);
      setPreviewOpen(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur d'aperçu"); }
    finally { setPreviewing(false); }
  };

  const doSend = async () => {
    if (!consent) { toast.error("Cochez la case de conformité (RGPD) avant d'envoyer."); return; }
    if (!invLabel.trim()) { toast.error("Donnez un nom à la campagne (ex. Presse, Amis…)."); return; }
    if (countEmails() === 0) { toast.error("Ajoutez au moins une adresse e-mail valide."); return; }
    let scheduled_at = null;
    if (scheduleOn) {
      if (!scheduleAt) { toast.error("Choisissez la date et l'heure d'envoi."); return; }
      scheduled_at = new Date(scheduleAt).toISOString();
    }
    setSending(true);
    try {
      const r = await api.post("/admin/ambassadors/invite", {
        emails: invEmails, label: invLabel.trim(), free_candles: Number(invFC) || 4,
        valid_hours: Number(invVH) || 48, subject: invSubject.trim() || null,
        consent_confirmed: true, scheduled_at,
      });
      toast.success(r.data.scheduled ? `Envoi programmé pour ${r.data.recipients} destinataire(s)` : `Invitations en cours d'envoi à ${r.data.recipients} destinataire(s)`);
      setInvEmails(""); setInvSubject(""); setConsent(false); setScheduleOn(false); setScheduleAt("");
      setPreviewOpen(false);
      loadBatches();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur d'envoi"); }
    finally { setSending(false); }
  };

  const deleteBatch = async (id) => {
    if (!window.confirm("Supprimer cette campagne d'invitation ? Cette action est définitive.")) return;
    try { await api.delete(`/admin/ambassadors/invites/${id}`); setBatches((x) => x.filter((b) => b.id !== id)); toast.success("Campagne supprimée"); }
    catch { toast.error("Suppression impossible"); }
  };

  const statusLabel = (s) => ({ scheduled: "Programmée", queued: "En file", sending: "Envoi en cours", sent: "Envoyée" }[s] || s);
  const recStatusLabel = (s) => ({ pending: "En attente", sent: "Envoyé", failed: "Échec", skipped: "Ignoré (désinscrit)" }[s] || s);

  return (
    <div className="space-y-5" data-testid="ambassador-panel">
      <div className="flex flex-wrap gap-2" data-testid="ambassador-view-tabs">
        <button type="button" onClick={() => setView("links")} className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${view === "links" ? "border-sky-400/60 bg-sky-500/15 text-sky-200" : "border-white/15 bg-white/5 text-muted-foreground hover:border-white/30"}`} data-testid="ambassador-tab-links">Liens & invitations</button>
        <button type="button" onClick={() => setView("press")} className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${view === "press" ? "border-amber-400/60 bg-amber-500/15 text-amber-200" : "border-white/15 bg-white/5 text-muted-foreground hover:border-white/30"}`} data-testid="ambassador-tab-press">Presse France</button>
        <button type="button" onClick={() => setView("crematorium")} className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${view === "crematorium" ? "border-amber-400/60 bg-amber-500/15 text-amber-200" : "border-white/15 bg-white/5 text-muted-foreground hover:border-white/30"}`} data-testid="ambassador-tab-crematorium">Crématorium</button>
        <button type="button" onClick={() => setView("funeral")} className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${view === "funeral" ? "border-amber-400/60 bg-amber-500/15 text-amber-200" : "border-white/15 bg-white/5 text-muted-foreground hover:border-white/30"}`} data-testid="ambassador-tab-funeral">Pompes funèbres</button>
      </div>

      {view === "press" ? <PressFrance /> : view === "crematorium" ? <Crematoriums /> : view === "funeral" ? <Funerals /> : (
      <>
      <div className="rounded-3xl border border-sky-400/20 bg-sky-500/[0.05] p-5">
        <div className="flex items-center gap-2 text-sky-300"><Award size={18} /><h2 className="text-lg font-semibold">Ambassadeurs de la marque</h2></div>
        <p className="mt-1 text-sm text-muted-foreground">Offre privée réservée à la direction — <strong>non visible du public</strong>. Générez un lien, envoyez-le directement à la personne : en ouvrant un compte dans les <strong>48 h</strong>, elle reçoit <strong>4 bougies gratuites pour l'éternité</strong>, à allumer quand elle le souhaite. Un nouveau lien peut être régénéré à tout moment.</p>
      </div>

      <Section icon={Plus} title="Générer un lien ambassadeur" desc="Choisissez la durée de validité AVANT de créer le lien. Le décompte démarre dès la génération : passé ce délai, l'ouverture d'un compte via ce lien ne donne plus les bougies. Le message prêt à envoyer valorise votre ambassadeur.">
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="sm:col-span-1">
            <Label>Nom du lien (requis)</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Ex. Famille, Amis, Presse…" data-testid="ambassador-label" />
          </div>
          <div>
            <Label>Validité (heures)</Label>
            <Input type="number" min={1} value={validHours} onChange={(e) => setValidHours(e.target.value)} data-testid="ambassador-valid-hours" />
          </div>
          <div>
            <Label>Bougies offertes</Label>
            <Input type="number" min={1} value={freeCandles} onChange={(e) => setFreeCandles(e.target.value)} data-testid="ambassador-free-candles" />
          </div>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="ambassador-presets">
          <span className="text-xs text-muted-foreground">Durées rapides :</span>
          {PRESETS.map((p) => (
            <button key={p.h} type="button" onClick={() => setValidHours(p.h)}
              className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${Number(validHours) === p.h ? "border-sky-400/60 bg-sky-500/15 text-sky-200" : "border-white/15 bg-white/5 text-muted-foreground hover:border-white/30"}`}
              data-testid={`ambassador-preset-${p.h}`}>
              {p.label}
            </button>
          ))}
        </div>
        <Button className="mt-3 rounded-full" onClick={createLink} disabled={creating} data-testid="ambassador-create-btn">
          {creating ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Plus size={15} className="mr-1.5" />} Générer le lien
        </Button>
      </Section>

      <Section icon={Link2} title="Liens générés" desc="Copiez le lien ou le message complet à envoyer directement à votre ambassadeur.">
        {loading ? (
          <p className="py-4 text-center text-sm text-muted-foreground">Chargement…</p>
        ) : links.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground" data-testid="ambassadors-empty">Aucun lien pour l'instant. Générez votre premier lien ambassadeur.</p>
        ) : (
          <div className="space-y-3" data-testid="ambassadors-list">
            {links.map((l) => (
              <div key={l.id} className="rounded-2xl border border-white/10 bg-black/20 p-4" data-testid="ambassador-item">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <code className="rounded bg-white/10 px-2 py-0.5 text-xs text-sky-200">{l.code}</code>
                      {l.label && <span className="text-sm font-medium text-foreground">{l.label}</span>}
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${l.expired || !l.active ? "bg-amber-400/15 text-amber-300" : "bg-emerald-500/15 text-emerald-300"}`} data-testid="ambassador-status">
                        {!l.active ? "Désactivé" : l.expired ? "Expiré" : "Actif"}
                      </span>
                      <span className="inline-flex items-center gap-1 rounded-full border border-sky-400/30 bg-sky-400/10 px-2 py-0.5 text-[11px] text-sky-300"><Flame size={11} /> {l.free_candles} éternelles</span>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1"><Clock size={12} /> Expire le {fmt(l.expires_at)}</span>
                      <InviteCountdown expiresAt={l.expires_at} validated={(l.signups || 0) > 0} />
                      <span className="inline-flex items-center gap-1" title="Clics"><MousePointerClick size={12} /> {l.clicks} clic(s)</span>
                      <span className="inline-flex items-center gap-1" title="Comptes ouverts"><Users size={12} /> {l.signups} compte(s)</span>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <Button size="sm" variant="ghost" className="h-8 text-foreground/70" onClick={() => renameLink(l)} title="Modifier le nom" data-testid="ambassador-rename-btn"><Pencil size={14} /></Button>
                    <Button size="sm" variant="ghost" className="h-8 text-sky-300" onClick={() => renew(l.id)} title="Relancer 48h" data-testid="ambassador-renew-btn"><RefreshCw size={14} /></Button>
                    <Button size="sm" variant="ghost" className="h-8 text-foreground/70" onClick={() => toggleActive(l)} data-testid="ambassador-toggle-btn">{l.active ? "Désactiver" : "Activer"}</Button>
                    <Button size="sm" variant="ghost" className="h-8 text-destructive" onClick={() => remove(l.id)} data-testid="ambassador-delete-btn"><Trash2 size={14} /></Button>
                  </div>
                </div>
                <div className="mt-2 flex items-center gap-2 rounded-lg border border-white/10 bg-black/30 px-2.5 py-1.5">
                  <Link2 size={13} className="shrink-0 text-sky-300" />
                  <code className="min-w-0 flex-1 truncate text-[11px] text-sky-200" data-testid="ambassador-link">{linkFor(l.code)}</code>
                  <button type="button" onClick={() => copy(linkFor(l.code), "Lien copié")} className="shrink-0 rounded-full p-1 text-muted-foreground hover:bg-white/10 hover:text-white" title="Copier le lien" data-testid="ambassador-copy-link"><Copy size={13} /></button>
                </div>
                <button type="button" onClick={() => copy(messageFor(l.code, l.free_candles, l.valid_hours), "Message copié — prêt à envoyer")} className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-sky-400/40 bg-sky-500/10 px-3 py-1.5 text-xs font-medium text-sky-200 transition-colors hover:bg-sky-500/20" data-testid="ambassador-copy-message">
                  <MessageSquareText size={13} /> Copier le message ambassadeur
                </button>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section icon={Mail} title="Inviter des ambassadeurs par e-mail" desc="Collez la liste d'e-mails, choisissez l'offre, puis envoyez (ou programmez) l'invitation. Chaque destinataire reçoit un LIEN UNIQUE : vous suivez qui a cliqué et qui a créé son compte." accent="text-amber-300">
        <div>
          <Label>Liste des e-mails (collez-les, séparés par virgule, espace ou retour à la ligne)</Label>
          <Textarea
            rows={5}
            value={invEmails}
            onChange={(e) => setInvEmails(e.target.value)}
            placeholder="jean@exemple.com, marie@exemple.com&#10;paul@exemple.com"
            className="mt-1 font-mono text-xs"
            data-testid="invite-emails"
          />
          <p className="mt-1 text-xs text-muted-foreground" data-testid="invite-count">{countEmails()} adresse(s) valide(s) détectée(s)</p>
        </div>

        <div className="mt-3 grid gap-3 sm:grid-cols-3">
          <div>
            <Label>Nom de la campagne (requis)</Label>
            <Input value={invLabel} onChange={(e) => setInvLabel(e.target.value)} placeholder="Ex. Presse, Amis, Partenaires…" data-testid="invite-label" />
          </div>
          <div>
            <Label>Bougies offertes</Label>
            <Input type="number" min={1} value={invFC} onChange={(e) => setInvFC(e.target.value)} data-testid="invite-free-candles" />
          </div>
          <div>
            <Label>Validité du lien (heures)</Label>
            <Input type="number" min={1} value={invVH} onChange={(e) => setInvVH(e.target.value)} data-testid="invite-valid-hours" />
          </div>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">Durées rapides :</span>
          {PRESETS.map((p) => (
            <button key={p.h} type="button" onClick={() => setInvVH(p.h)}
              className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${Number(invVH) === p.h ? "border-amber-400/60 bg-amber-500/15 text-amber-200" : "border-white/15 bg-white/5 text-muted-foreground hover:border-white/30"}`}>
              {p.label}
            </button>
          ))}
        </div>

        <div className="mt-3">
          <Label>Objet de l'e-mail (facultatif)</Label>
          <Input value={invSubject} onChange={(e) => setInvSubject(e.target.value)} placeholder="Votre invitation privée — Ambassadeur BougieDuCœur" data-testid="invite-subject" />
        </div>

        <label className="mt-3 flex cursor-pointer items-start gap-2 rounded-xl border border-amber-400/20 bg-amber-500/[0.05] p-3 text-xs text-amber-100/90" data-testid="invite-schedule-toggle">
          <input type="checkbox" checked={scheduleOn} onChange={(e) => setScheduleOn(e.target.checked)} className="mt-0.5 h-4 w-4 accent-amber-400" />
          <span className="flex items-center gap-1.5"><CalendarClock size={14} /> Programmer l'envoi à une date/heure précise</span>
        </label>
        {scheduleOn && (
          <div className="mt-2">
            <Label>Date et heure d'envoi</Label>
            <Input type="datetime-local" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)} className="mt-1 max-w-xs" data-testid="invite-schedule-at" />
          </div>
        )}

        <label className="mt-3 flex cursor-pointer items-start gap-2 rounded-xl border border-white/10 bg-black/20 p-3 text-xs text-muted-foreground" data-testid="invite-consent">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 h-4 w-4 accent-sky-400" data-testid="invite-consent-checkbox" />
          <span className="flex items-start gap-1.5"><ShieldCheck size={14} className="mt-0.5 shrink-0 text-sky-300" /> Je certifie disposer du consentement ou d'une relation existante avec ces destinataires, conformément au RGPD. Chaque e-mail inclut l'identité de l'expéditeur et un lien de désinscription.</span>
        </label>

        <div className="mt-4 flex flex-wrap gap-2">
          <Button variant="outline" className="rounded-full border-white/15" onClick={doPreview} disabled={previewing || countEmails() === 0} data-testid="invite-preview-btn">
            {previewing ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Eye size={15} className="mr-1.5" />} Aperçu
          </Button>
          <Button className="rounded-full" onClick={doSend} disabled={sending || !consent} data-testid="invite-send-btn">
            {sending ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : scheduleOn ? <CalendarClock size={15} className="mr-1.5" /> : <Send size={15} className="mr-1.5" />}
            {scheduleOn ? "Programmer l'envoi" : "Envoyer maintenant"}
          </Button>
        </div>
      </Section>

      <Section icon={Send} title="Campagnes d'invitation" desc="Suivi des invitations envoyées : statut par destinataire, clics et comptes créés.">
        {batches.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground" data-testid="invites-empty">Aucune campagne d'invitation pour l'instant.</p>
        ) : (
          <div className="space-y-3" data-testid="invites-list">
            {batches.map((b) => (
              <div key={b.id} className="rounded-2xl border border-white/10 bg-black/20 p-4" data-testid="invite-batch">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-foreground">{b.label}</span>
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${b.status === "sent" ? "bg-emerald-500/15 text-emerald-300" : b.status === "scheduled" ? "bg-amber-400/15 text-amber-300" : "bg-sky-500/15 text-sky-300"}`} data-testid="invite-batch-status">{statusLabel(b.status)}</span>
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1"><Users size={12} /> {b.total} invité(s)</span>
                      <span className="inline-flex items-center gap-1"><MousePointerClick size={12} /> {b.clicked} clic(s)</span>
                      <span className="inline-flex items-center gap-1"><CheckCircle2 size={12} /> {b.signed_up} inscrit(s)</span>
                      {b.scheduled_at && <span className="inline-flex items-center gap-1"><CalendarClock size={12} /> {fmt(b.scheduled_at)}</span>}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <Button size="sm" variant="ghost" className="h-8 text-foreground/70" onClick={() => setExpanded(expanded === b.id ? null : b.id)} data-testid="invite-batch-expand">
                      {expanded === b.id ? <ChevronDown size={15} /> : <ChevronRight size={15} />} Détails
                    </Button>
                    <Button size="sm" variant="ghost" className="h-8 text-destructive" onClick={() => deleteBatch(b.id)} data-testid="invite-batch-delete"><Trash2 size={14} /></Button>
                  </div>
                </div>
                {expanded === b.id && (
                  <div className="mt-3 space-y-1 border-t border-white/10 pt-3" data-testid="invite-batch-recipients">
                    {(b.recipients || []).map((r, i) => (
                      <div key={i} className="flex items-center justify-between gap-2 text-xs">
                        <span className="min-w-0 truncate text-foreground/80">{r.email}</span>
                        <span className="flex shrink-0 items-center gap-2">
                          <InviteCountdown expiresAt={r.expires_at} validated={r.validated} />
                          <span className="text-muted-foreground">{r.clicks} clic(s)</span>
                          <span className={`rounded-full px-2 py-0.5 ${r.status === "sent" ? "bg-emerald-500/15 text-emerald-300" : r.status === "failed" ? "bg-destructive/20 text-red-300" : "bg-white/10 text-muted-foreground"}`}>{recStatusLabel(r.status)}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Section>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="glass max-h-[85vh] overflow-y-auto border-white/10 sm:max-w-lg" data-testid="invite-preview-dialog">
          <DialogHeader><DialogTitle className="text-lg font-medium">Aperçu de l'invitation</DialogTitle></DialogHeader>
          {preview && (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2 text-xs">
                <span className="rounded-full bg-emerald-500/15 px-2.5 py-1 text-emerald-300" data-testid="preview-valid">{preview.valid_count} valide(s)</span>
                {preview.invalid_count > 0 && <span className="rounded-full bg-amber-400/15 px-2.5 py-1 text-amber-300" data-testid="preview-invalid">{preview.invalid_count} invalide(s)</span>}
                {preview.suppressed_count > 0 && <span className="rounded-full bg-white/10 px-2.5 py-1 text-muted-foreground" data-testid="preview-suppressed">{preview.suppressed_count} désinscrit(s)</span>}
                {preview.already_count > 0 && <span className="rounded-full bg-sky-500/15 px-2.5 py-1 text-sky-300" data-testid="preview-already">{preview.already_count} déjà invité(s)</span>}
              </div>
              {preview.invalid_count > 0 && (
                <p className="text-xs text-muted-foreground">Ignorés : {preview.invalid_samples.join(", ")}</p>
              )}
              <p className="text-xs text-muted-foreground">Objet : <span className="text-foreground/90">{preview.preview_subject}</span></p>
              <div className="overflow-hidden rounded-xl border border-white/10 bg-white">
                <iframe title="preview-email" srcDoc={preview.preview_html} className="h-[420px] w-full" data-testid="preview-iframe" />
              </div>
              <Button className="w-full rounded-full" onClick={doSend} disabled={sending || !consent} data-testid="invite-preview-send-btn">
                {sending ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Send size={15} className="mr-1.5" />}
                {!consent ? "Cochez la conformité RGPD pour envoyer" : scheduleOn ? "Programmer l'envoi" : "Envoyer maintenant"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      </>
      )}
    </div>
  );
}

export default AmbassadorPanel;
